/** @format */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail, FiUser } from 'react-icons/fi';

import Header from '../../components/common/Header';
// lazy load footer
const Footer = React.lazy(() => import('../../components/common/Footer'));
import TextInput from '../../components/ui/TextInput';
import TextAreaInput from '../../components/ui/TextAreaInput';
import { AuthButton } from '../../components/common/AuthButton';

const ContactUs: React.FC = () => {
	const navigate = useNavigate();

	const [form, setForm] = useState({
		fullName: '',
		emailId: '',
		message: '',
	});

	const [errors, setErrors] = useState({
		fullName: '',
		emailId: '',
		message: '',
	});

	const [loading, setLoading] = useState(false);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setForm((prev) => ({ ...prev, [name]: value }));

		if (errors[name as keyof typeof errors]) {
			setErrors((prev) => ({ ...prev, [name]: '' }));
		}
	};

	const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setForm((prev) => ({ ...prev, message: e.target.value }));
		if (errors.message) {
			setErrors((prev) => ({ ...prev, message: '' }));
		}
	};

	const validateField = (
		fieldName: keyof typeof form,
		value: string
	): string => {
		if (!value.trim()) return 'This field is required';

		if (fieldName === 'emailId' && !/\S+@\S+\.\S+/.test(value)) {
			return 'Please enter a valid email address';
		}

		if (fieldName === 'fullName' && value.length < 2) {
			return 'Name must be at least 2 characters';
		}

		if (fieldName === 'message' && value.length < 10) {
			return 'Message must be at least 10 characters';
		}

		return '';
	};

	const validateForm = () => {
		let isValid = true;
		const newErrors = { ...errors };

		(Object.keys(form) as (keyof typeof form)[]).forEach((key) => {
			const error = validateField(key, form[key]);
			newErrors[key] = error;
			if (error) isValid = false;
		});

		setErrors(newErrors);
		return isValid;
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!validateForm()) return;
		setLoading(true);
	};

	const isFormValid =
		form.fullName &&
		form.emailId &&
		form.message &&
		Object.values(errors).every((e) => !e);

	return (
		<body className='bg-white p-0! overflow-x-hidden'>
			<Header />

			{/* Page Wrapper */}
			<main className='py-16 px-4 bg-[#F6FBFD]'>
				{/* Back Button */}
				<div className='max-w-3xl mx-auto mb-6'>
					<button
						onClick={() => navigate(-1)}
						aria-label='Back'
						className='bg-myButtonBg hover:bg-myLabelText text-white px-5 py-2 rounded-md transition'>
						← Back
					</button>
				</div>

				{/* Form Section */}
				<section className='max-w-3xl mx-auto bg-white rounded-xl shadow-md p-6 sm:p-8'>
					<h1 className='text-3xl font-bold text-myButtonBg mb-2'>
						Contact Us
					</h1>
					<p className='text-gray-600 mb-6'>
						We’d love to hear from you. Please fill out the form below.
					</p>

					<form
						onSubmit={handleSubmit}
						className='space-y-4'>
						<div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
							<TextInput
								id='fullName'
								name='fullName'
								label='Full Name'
								type='text'
								placeholder='Enter your full name'
								value={form.fullName}
								onChange={handleChange}
								errorMessage={errors.fullName}
								icon={FiUser}
								disabled={loading}
								required
							/>

							<TextInput
								id='emailId'
								name='emailId'
								label='Email Address'
								type='email'
								placeholder='Enter your email'
								value={form.emailId}
								onChange={handleChange}
								errorMessage={errors.emailId}
								icon={FiMail}
								disabled={loading}
								required
							/>
						</div>

						<TextAreaInput
							rows={4}
							id='message'
							name='message'
							label='Message'
							placeholder='Type your message here...'
							value={form.message}
							onChange={handleTextAreaChange}
							error={!!errors.message}
							errorMessage={errors.message}
							disabled={loading}
							required
						/>

						<AuthButton
							title='Send Message'
							loadingTitle='Sending...'
							loading={loading}
							disabled={!isFormValid || loading}
						/>
					</form>
				</section>

				{/* Contact Info */}
				<section className='max-w-3xl mx-auto mt-10 bg-white rounded-xl shadow-md p-6 sm:p-8'>
					<div className='grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm'>
						<div>
							<h3 className='font-semibold text-gray-900 tracking-widest text-xs'>
								EMAIL
							</h3>
							<Link
								to='mailto:support@llb360.com'
								className='text-myButtonBg hover:underline'>
								support@llb360.com
							</Link>
						</div>

						<div>
							<h3 className='font-semibold text-gray-900 tracking-widest text-xs'>
								PHONE
							</h3>
							<Link
								to='tel:+917673937360'
								className='text-myButtonBg hover:underline'>
								+91 76739 37360
							</Link>
						</div>

						<div className='sm:col-span-2'>
							<h3 className='font-semibold text-gray-900 tracking-widest text-xs'>
								ADDRESS
							</h3>
							<p className='text-gray-600 leading-relaxed mt-1'>
								Saadruso Internet Media Pvt Ltd.
								<br />
								Plot #28, Doyens Township,
								<br />
								Serilingampally, Hyderabad – 500019
							</p>
						</div>
					</div>
				</section>
			</main>

			<React.Suspense
				fallback={<div className='h-20 bg-gray-100 animate-pulse' />}>
				<Footer />
			</React.Suspense>
		</body>
	);
};

export default ContactUs;
